import { Component, Host, h, Prop, EventEmitter, Event, Watch, Method, Element, AttachInternals } from '@stencil/core';
import 'element-internals-polyfill';

let id = 0;

/**
 * @slot - Main content of element.
 *
 * @part checkbox - Main checkbox container.
 * @part label - Label element.
 * @part input - Input element.
 * @part text - Label text.
 */
@Component({
  tag: 'o2-checkbox',
  styleUrl: 'checkbox.scss',
  shadow: true,
  formAssociated: true,
})
export class O2Checkbox {
  private inputId = `o2-checkbox-${++id}`;
  private labelId = `${this.inputId}-label`;
  private input: HTMLInputElement;

  private hasTextSlot: boolean;

  @AttachInternals() internals: ElementInternals;

  @Element() private el!: HTMLO2CheckboxElement;

  /**
   * Sets the value of checkbox input
   */
  @Prop({ reflect: true }) value?: string;

  /**
   * Sets the name of checkbox input
   */
  @Prop() name?: string = this.inputId;

  /**
   * Sets the checked attribute of checkbox input
   */
  @Prop({ mutable: true, reflect: true }) checked?: boolean = false;

  /**
   * Sets indeterminate state of input
   */
  @Prop({ mutable: true, reflect: true }) indeterminate?: boolean = false;

  /**
   * Sets checkbox disabled
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Emitted when checkbox is changed.
   */
  @Event() o2Change!: EventEmitter<any>;

  /**
   * Emits checkbox change.It is consumed by o2-checkbox-group
   */
  @Event() o2CheckboxChange!: EventEmitter<any>;

  /**
   * Emitted when the input loses focus.
   */
  @Event() o2Blur!: EventEmitter<FocusEvent>;

  /**
   * Watches checked and indeterminate property
   */
  @Watch('checked')
  @Watch('indeterminate')
  handleCheckedChange() {
    this.input.checked = this.checked;
    this.input.indeterminate = this.indeterminate;

    this.internals.setFormValue(this.checked ? (this.value ? this.value : 'on') : undefined, this.checked ? 'checked' : undefined);
    this.internals.ariaChecked = this.checked.toString();

    this.el.ariaChecked = this.checked.toString();
  }

  /**
   * Watches disabled property
   */
  @Watch('disabled')
  handleDisabledChange(newVal: boolean) {
    this.internals.ariaDisabled = newVal.toString();
    this.el.ariaDisabled = newVal.toString();
  }

  /**
   * Watches required property
   */
  @Watch('required')
  handleRequiredChange(newVal: boolean) {
    this.el.ariaRequired = newVal.toString();
  }

  /**
   * Sets focus on the native `input` in `o2-checkbox`.
   */
  @Method()
  async setFocus() {
    if (this.input) {
      this.input.focus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-checkbox`.
   * @internal
   */
  @Method()
  async setBlur() {
    if (this.input) {
      this.input.blur();
    }
  }

  private handleChange = () => {
    this.checked = this.input.checked;
    this.indeterminate = false;

    this.internals.setFormValue(this.checked ? (this.value ? this.value : 'on') : undefined, this.checked ? 'checked' : undefined);
    this.internals.ariaChecked = this.checked.toString();

    this.o2Change.emit({ value: this.checked });
    this.o2CheckboxChange.emit({ value: this.checked });
  };

  private onBlur = event => {
    event.stopPropagation();
    this.o2Blur.emit();
  };

  connectedCallback() {
    // check if wc has at least one node
    this.hasTextSlot = !!this.el.childNodes.length;

    this.el.ariaDisabled = this.disabled.toString();
    this.el.ariaRequired = this.required.toString();
  }

  componentWillLoad() {
    this.internals.form && this.internals.setFormValue(this.checked ? (this.value ? this.value : 'on') : undefined, this.checked ? 'checked' : undefined);
    this.internals.ariaChecked = this.checked.toString();
    this.internals.ariaDisabled = this.disabled.toString();
    this.internals.ariaRequired = this.required.toString();
    this.internals.role = 'checkbox';
  }

  render() {
    return (
      <Host role="checkbox" aria-checked={this.checked.toString()} aria-hidden={this.disabled ? 'true' : null} aria-labelledby={this.labelId ? this.labelId : null}>
        <div
          part="checkbox"
          class={`
            checkbox
            ${this.disabled ? 'checkbox--disabled' : ''}
            ${this.checked ? 'checkbox--checked' : ''}
            ${this.error ? 'checkbox--error' : ''}`}
        >
          <label part="label" class="checkbox__label" htmlFor={this.inputId} id={this.labelId}>
            <input
              part="input"
              ref={el => (this.input = el)}
              class={`checkbox__input ${this.error ? 'checkbox__input--error' : ''} ${this.disabled ? 'checkbox__input--disabled' : ''}`}
              type="checkbox"
              id={this.inputId}
              name={this.name}
              value={this.value}
              disabled={this.disabled}
              onChange={this.handleChange}
              indeterminate={this.indeterminate}
              checked={this.checked}
              onBlur={this.onBlur}
              required={this.required}
            />
            {/* TODO: render svg icon based on state */}
            <svg class="checkbox__icon checkbox__icon--check" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 8.19048L5.87097 12L14 4" stroke="white" stroke-width="2" stroke-linecap="round" />
            </svg>
            <svg class="checkbox__icon checkbox__icon--minus" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 8L14 8" stroke="white" stroke-width="2" stroke-linecap="round" />
            </svg>
            {this.hasTextSlot && (
              <div class="checkbox__text" part="text">
                <slot></slot>
              </div>
            )}
          </label>
        </div>
      </Host>
    );
  }
}
